"use client";

import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { useSearchParams } from 'next/navigation';
import { Prompt } from "@/types/assessment.types";
import AssessmentWrapper from './AssessmentWrapper';
import procrastinationQuizMoreInfoFlow from '@timeblock/assessment-content/assessments/procrastination-quiz/flow-more-info.json';
import { procrastinationQuizStepByStepFlow } from '@timeblock/assessment-content/assessments/procrastination-quiz/flow-config';

type Props = {
  prompt: Prompt;
  assessmentType: string;
};

export default function ProcrastinationQuizABTest({ prompt, assessmentType }: Props) {
  const searchParams = useSearchParams();
  const [variant, setVariant] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for URL parameter override
  const urlVariant = searchParams.get('variant');

  useEffect(() => {
    // If URL variant is specified, use it directly (for testing)
    if (urlVariant === 'test' || urlVariant === 'control') {
      setVariant(urlVariant);
      setIsLoading(false);
      return;
    }

    // Check if PostHog is already loaded and has feature flags
    if (typeof posthog.getFeatureFlag === 'function') {
      const flagValue = posthog.getFeatureFlag('procrastination-quiz-more-info-flow');
      console.log('[ProcrastinationQuizABTest] More info flag value (immediate):', flagValue);
      setVariant(flagValue as string);
      setIsLoading(false);
    } else {
      // Wait for feature flags to load
      posthog.onFeatureFlags(() => {
        const flagValue = posthog.getFeatureFlag('procrastination-quiz-more-info-flow');
        console.log('[ProcrastinationQuizABTest] More info flag value (after load):', flagValue);
        console.log('[ProcrastinationQuizABTest] PostHog distinct ID:', posthog.get_distinct_id());
        setVariant(flagValue as string);
        setIsLoading(false);
      });
    }
  }, [urlVariant]);

  // Track experiment exposure when variant is determined and component renders
  useEffect(() => {
    if (variant !== null && !isLoading) {
      posthog.capture('$feature_flag_called', {
        '$feature_flag': 'procrastination-quiz-more-info-flow',
        '$feature_flag_response': variant
      });
    }
  }, [variant, isLoading]);

  if (isLoading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  // Determine which variant to show
  // Only default to control if variant is null (not assigned by PostHog)
  const finalVariant = variant === null ? 'control' : variant;

  // Show more-info flow for test variant
  if (finalVariant === 'test') {
    return (
      <AssessmentWrapper 
        prompt={prompt} 
        assessmentType={assessmentType}
        flow={procrastinationQuizMoreInfoFlow as any}
      />
    );
  }

  // Show step-by-step flow for control variant
  return (
    <AssessmentWrapper 
      prompt={prompt} 
      assessmentType={assessmentType}
      flow={procrastinationQuizStepByStepFlow as any}
    />
  );
}